import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Permissions } from '@/auth/permissions';
import { useHasPermission } from '@/auth/RequirePermission';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { NewEstimateDialog } from '@/components/estimates/NewEstimateDialog';
import { formatCurrency, formatDateOnly } from '@/lib/format';
import { ESTIMATE_STATUSES, useEstimates } from '@/routes/estimates/useEstimates';

/**
 * The estimates list (M2-06): the office's way back to an estimate, and a
 * technician's list of the ones on their jobs (the API narrows it).
 * "New estimate" here is the office's — it picks a customer and property; a
 * technician starts one from the job they are on.
 */
export function Estimates() {
  const { t } = useTranslation();
  const hasPermission = useHasPermission();
  const canCreate = hasPermission(Permissions.manageCustomers);
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const estimates = useEstimates({ status });

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-xl font-semibold">{t('estimates.title')}</h1>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('estimates.statusFilter')}</span>
            <select
              className={FIELD_CLASS}
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">{t('estimates.allStatuses')}</option>
              {ESTIMATE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`estimates.status.${s}`)}
                </option>
              ))}
            </select>
          </label>
          {canCreate ? (
            <Button onClick={() => setCreating(true)}>{t('estimates.new')}</Button>
          ) : null}
        </div>
      </div>

      {estimates.isPending ? (
        <LoadingState label={t('estimates.loading')} />
      ) : estimates.isError ? (
        <ErrorState
          title={t('estimates.loadErrorTitle')}
          description={t('estimates.loadErrorDescription')}
          onRetry={() => void estimates.refetch()}
        />
      ) : estimates.data.length === 0 ? (
        <EmptyState title={status ? t('estimates.emptyFiltered') : t('estimates.empty')} />
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-[var(--color-muted-foreground)]">
            <tr>
              <th scope="col" className="py-2 pr-3 font-medium">
                {t('estimates.columns.estimate')}
              </th>
              <th scope="col" className="py-2 pr-3 font-medium">
                {t('estimates.columns.customer')}
              </th>
              <th scope="col" className="py-2 pr-3 font-medium">
                {t('estimates.columns.status')}
              </th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">
                {t('estimates.columns.total')}
              </th>
              <th scope="col" className="hidden py-2 font-medium sm:table-cell">
                {t('estimates.columns.expires')}
              </th>
            </tr>
          </thead>
          <tbody>
            {estimates.data.map((e) => {
              const number = Number(e.number);
              const revision = Number(e.revision);
              return (
                <tr
                  key={e.id}
                  className="border-t border-[var(--color-border)]"
                  data-testid="estimate-row"
                >
                  <td className="py-2 pr-3">
                    <Link to={`/estimates/${e.id}`} className="font-medium hover:underline">
                      {revision > 1
                        ? t('estimates.numberedRevision', { number, revision })
                        : t('estimates.numbered', { number })}
                    </Link>
                  </td>
                  <td className="py-2 pr-3">{e.customerName}</td>
                  <td className="py-2 pr-3">
                    {t(`estimates.status.${e.status}`, { defaultValue: e.status })}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {formatCurrency(Number(e.total))}
                  </td>
                  <td className="hidden py-2 sm:table-cell">
                    {e.expiresOn ? formatDateOnly(e.expiresOn) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {creating ? <NewEstimateDialog onClose={() => setCreating(false)} /> : null}
    </section>
  );
}
